import {
	addDays,
	format,
	getDate,
	isAfter,
	isBefore,
	isEqual,
	startOfMonth,
	subDays,
} from 'date-fns';
import type { CalendarStatus } from '../../calendar';
import type { PlanStore } from '@micro-apps/stores';

export function isSameOrAfter(date1: Date, date2?: Date | null): boolean {
	if (!date2) return false;
	return isAfter(date1, date2) || isEqual(date1, date2);
}

export function getCalendarStatus(day: Date, planStore?: PlanStore): CalendarStatus {
	const firstOfMonth = format(startOfMonth(day), 'yyyy-MM-dd');
	const availability = planStore?.calendarResult?.calendar?.[0]?.availability?.find(
		a => a.date === firstOfMonth
	);
	if (availability?.status) {
		return availability.status.split('')[getDate(day) - 1] as CalendarStatus;
	}
	return '?';
}

/** Stay night is selectable unless it is outside the window (.) or has no status (?). Sold out (0), restricted (R), and available (1-9) are allowed. */
export function isStayNightBookable(status: CalendarStatus): boolean {
	return status !== '.' && status !== '?';
}

/** True when every night in [checkIn, checkOut) is an allowed stay night. The checkout day itself is not checked. */
export function isValidCheckoutForStay(
	checkIn: Date,
	checkOut: Date,
	planStore?: PlanStore
): boolean {
	if (!isAfter(checkOut, checkIn)) {
		return false;
	}
	let day = checkIn;
	while (isBefore(day, checkOut)) {
		if (!isStayNightBookable(getCalendarStatus(day, planStore))) {
			return false;
		}
		day = addDays(day, 1);
	}
	return true;
}

/**
 * Last selectable checkout date: earliest of the max-stay limit and lastCheckoutDate.
 * maxEnd is exclusive (same as prior CAW). Sold-out nights do not shorten the range.
 */
export function getMaxCheckoutDate(
	checkIn: Date,
	planStore?: PlanStore,
	maxEnd?: Date | null
): Date | null {
	const maxStayLastSelectable = maxEnd && isAfter(maxEnd, checkIn) ? subDays(maxEnd, 1) : null;
	const candidates = [maxStayLastSelectable, planStore?.config.lastCheckoutDate].filter(
		(d): d is Date => !!d
	);
	if (!candidates.length) {
		return null;
	}
	return candidates.reduce((earliest, next) => (isBefore(next, earliest) ? next : earliest));
}
